/*
 Copyright (c), CKSource Holding sp. z o.o. All rights reserved.
 */

import Message, { IMessage } from './../../message';
import OperationMetadataHelpers from './../helpers/operationmetadatahelpers';

import { IDescriptorBody } from '../../descriptor';
import OperationMetadataDescriptor, { IOperationMetadata, IOperationMetadataJSON } from '../descriptors/operationmetadatadescriptor';

export interface ICollaborativeEditingUpdateMessage extends IMessage {
	id: string;
	buffers: Buffer[] | Uint8Array[];
	types: number[];
	baseVersion: number;
	metadata: IOperationMetadataJSON[];
	lastOperationId?: string;
}

interface ICollaborativeEditingUpdateData {
	buffers: Buffer[] | Uint8Array[];
	types: number[];
	baseVersion: number;
	lastOperationId?: string;
}

export default class CollaborativeEditingUpdateMessage extends Message {
	public static readonly TYPE: string = '13';

	public static readonly READABLE_TYPE_NAME: string = 'updateDocument';

	public static readonly DESCRIPTOR_NAME: string = 'CollaborativeEditingUpdateMessage';

	public static readonly DESCRIPTOR: IDescriptorBody = {
		fields: {
			id: {
				type: 'string',
				id: 1
			},
			baseVersion: {
				type: 'uint32',
				id: 2
			},
			buffers: {
				rule: 'repeated',
				type: 'bytes',
				id: 3
			},
			types: {
				rule: 'repeated',
				type: 'uint32',
				id: 4
			},
			metadata: {
				rule: 'repeated',
				type: 'OperationMetadataDescriptor',
				id: 6
			},
			lastOperationId: {
				type: 'string',
				id: 7
			}
		}
	};

	public constructor(
		public readonly id: string,
		public readonly buffers: Buffer[] | Uint8Array[],
		public readonly types: number[],
		public readonly baseVersion: number,
		public readonly metadata: IOperationMetadata[] = [],
		public readonly lastOperationId: string = ''
	) {
		super();
	}

	/**
	 * Returns compressed data of the operations.
	 */
	public get data(): ICollaborativeEditingUpdateData {
		return {
			buffers: this.buffers,
			types: this.types,
			baseVersion: this.baseVersion,
			lastOperationId: this.lastOperationId
		};
	}

	public toJSON(): ICollaborativeEditingUpdateMessage {
		return {
			id: this.id,
			buffers: this.buffers,
			types: this.types,
			baseVersion: this.baseVersion,
			metadata: OperationMetadataHelpers
				.removeUnnecessaryMetadata( this.types, this.metadata )
				.map( OperationMetadataDescriptor.toJSON ),
			lastOperationId: this.lastOperationId
		};
	}

	public static fromJSON( data: ICollaborativeEditingUpdateMessage ): CollaborativeEditingUpdateMessage {
		return new CollaborativeEditingUpdateMessage(
			data.id,
			data.buffers,
			data.types,
			data.baseVersion,
			OperationMetadataHelpers
				.prepareMetadataForOperations( data.types, data.metadata )
				.map( OperationMetadataDescriptor.fromJSON ),
			data.lastOperationId
		);
	}
}
